import React, { useState, useEffect, useMemo } from "react";
import { Plus, X, Users, UserCheck, UserPlus, Check, AlertCircle } from "lucide-react";
import SearchableSelect, { SelectOption } from "../../ui/SearchableSelect";
import { clientsApi } from "../../../services/api";
import { useUserDataContext } from "../../../context/UserDataContext";

// ─── Types ────────────────────────────────────────────────────────────────────

interface Signer {
  name: string;
  email: string;
  role: string;
  order: number;
  clientId?: string;
  color: string;
  createAsClient?: boolean;
}

interface WizardData {
  matterId: string;
  templateId: string;
  subject: string;
  message: string;
  documents: any[];
  signers: Signer[];
  fields: any[];
  sequentialSigning: boolean;
  expirationDays: number;
  reminders: { enabled: boolean; intervalDays: number; maxReminders: number };
  saveAsTemplate: boolean;
  templateName: string;
}

interface SignersStepProps {
  data: WizardData;
  onChange: (data: WizardData) => void;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const SIGNER_COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899"];

const ROLE_OPTIONS: SelectOption[] = [
  { value: "client", label: "Client" },
  { value: "lawyer", label: "Lawyer" },
  { value: "witness", label: "Witness" },
  { value: "notary", label: "Notary" },
  { value: "other", label: "Other" },
];

// ─── Component ────────────────────────────────────────────────────────────────

const SignersStep: React.FC<SignersStepProps> = ({ data, onChange }) => {
  const { userData } = useUserDataContext();
  const [clientOptions, setClientOptions] = useState<SelectOption[]>([]);
  const [clientEmailMap, setClientEmailMap] = useState<Map<string, string>>(new Map());
  const [loadingClients, setLoadingClients] = useState(false);
  const isAlreadyAdded = useMemo(
    () =>
      data.signers.some(
        (s) => s.email.toLowerCase() === userData.email.toLowerCase()
      ),
    [data.signers, userData.email]
  );

  // Fetch clients
  useEffect(() => {
    setLoadingClients(true);
    clientsApi
      .list({ limit: 100 })
      .then((res) => {
        const clients = res.data?.data || res.data || [];
        const emailMap = new Map<string, string>();
        const opts = clients.map((c: any) => {
          const email =
            c.emails?.find((e: any) => e.label === "main")?.email ||
            c.emails?.[0]?.email ||
            c.email ||
            "";
          const displayName = c.displayName || c.name || `${c.firstName || ""} ${c.lastName || ""}`.trim() || c._id;
          if (email) {
            emailMap.set(email.toLowerCase(), displayName);
          }
          // Also index all emails
          if (c.emails) {
            for (const e of c.emails) {
              if (e.email) emailMap.set(e.email.toLowerCase(), displayName);
            }
          }
          return {
            value: c._id,
            label: displayName,
            description: email || undefined,
          };
        });
        setClientOptions(opts);
        setClientEmailMap(emailMap);
      })
      .catch(() => {})
      .finally(() => setLoadingClients(false));
  }, []);

  const updateSigner = (index: number, updates: Partial<Signer>) => {
    const updated = [...data.signers];
    updated[index] = { ...updated[index], ...updates };
    onChange({ ...data, signers: updated });
  };

  const addSigner = () => {
    const nextColor = SIGNER_COLORS[data.signers.length % SIGNER_COLORS.length];
    onChange({
      ...data,
      signers: [
        ...data.signers,
        {
          name: "",
          email: "",
          role: "client",
          order: data.signers.length + 1,
          color: nextColor,
        },
      ],
    });
  };

  const addMyself = () => {
    if (isAlreadyAdded) return;
    const nextColor = SIGNER_COLORS[data.signers.length % SIGNER_COLORS.length];
    onChange({
      ...data,
      signers: [
        ...data.signers,
        {
          name: userData.name,
          email: userData.email,
          role: "lawyer",
          order: data.signers.length + 1,
          color: nextColor,
        },
      ],
    });
  };

  const removeSigner = (index: number) => {
    if (data.signers.length <= 1) return;
    onChange({
      ...data,
      signers: data.signers.filter((_, i) => i !== index),
    });
  };

  const handleClientSelect = (index: number, clientId: string | null) => {
    if (!clientId) {
      updateSigner(index, { clientId: undefined, name: "", email: "" });
      return;
    }

    // Find client in options to auto-fill
    const clientOpt = clientOptions.find((o) => o.value === clientId);
    if (clientOpt) {
      updateSigner(index, {
        clientId,
        name: clientOpt.label,
        email: clientOpt.description || "",
      });
    } else {
      updateSigner(index, { clientId });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-slate-500" />
          <h3 className="text-sm font-semibold text-slate-700">Signers</h3>
          <span className="text-xs text-slate-400">
            ({data.signers.length} signer{data.signers.length !== 1 ? "s" : ""})
          </span>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={addMyself}
            disabled={isAlreadyAdded}
            className={`flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-lg transition-colors ${
              isAlreadyAdded
                ? "bg-emerald-50 text-emerald-600 cursor-default"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            {isAlreadyAdded ? (
              <>
                <Check className="h-3.5 w-3.5" />
                Already Added
              </>
            ) : (
              <>
                <UserPlus className="h-3.5 w-3.5" />
                Add Myself
              </>
            )}
          </button>
          <button
            type="button"
            onClick={addSigner}
            className="flex items-center gap-1 text-xs font-medium transition-colors"
            style={{ color: "var(--color-primary)" }}
          >
            <Plus className="h-3.5 w-3.5" />
            Add Signer
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {data.signers.map((signer, i) => (
          <div
            key={i}
            className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm"
          >
            {/* Card header with colored left border */}
            <div
              className="flex items-center justify-between px-4 py-3 border-b border-slate-100"
              style={{ borderLeft: `4px solid ${signer.color}` }}
            >
              <div className="flex items-center gap-2">
                <div
                  className="h-3 w-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: signer.color }}
                />
                <span className="text-sm font-semibold text-slate-700">
                  Signer {i + 1}
                </span>
              </div>
              {data.signers.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeSigner(i)}
                  className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Card body */}
            <div className="p-4 space-y-3">
              {/* Contact selector */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-medium text-slate-500">
                    Select from contacts
                  </label>
                </div>
                <SearchableSelect
                  options={clientOptions}
                  value={signer.clientId || null}
                  onChange={(val) => handleClientSelect(i, val)}
                  placeholder="Search contacts to auto-fill..."
                  searchPlaceholder="Search by name or email..."
                  loading={loadingClients}
                  clearable
                />
              </div>

              {/* Name field */}
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={signer.name}
                  onChange={(e) => updateSigner(i, { name: e.target.value, clientId: undefined })}
                  placeholder="Full name"
                  className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:border-emerald-600 transition-colors"
                />
              </div>

              {/* Email field */}
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={signer.email}
                  onChange={(e) => updateSigner(i, { email: e.target.value })}
                  placeholder="Email address"
                  className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:border-emerald-600 transition-colors"
                />
                {/* Existing client match notification */}
                {signer.email && !signer.clientId && clientEmailMap.has(signer.email.toLowerCase()) && (
                  <div className="flex items-center gap-2 mt-1.5 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                    <UserCheck className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />
                    <span className="text-xs text-blue-700">
                      This email belongs to an existing client: <span className="font-semibold">{clientEmailMap.get(signer.email.toLowerCase())}</span>
                    </span>
                  </div>
                )}
                {/* Create as client checkbox */}
                {!signer.clientId && signer.name && signer.email && !clientEmailMap.has(signer.email.toLowerCase()) && (
                  <label className="flex items-center gap-2 mt-2 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={!!signer.createAsClient}
                      onChange={(e) => updateSigner(i, { createAsClient: e.target.checked })}
                      className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <span className="text-xs text-slate-500 group-hover:text-slate-700 transition-colors">
                      Also create as a new {signer.role === "client" ? "client" : "contact"} in your CRM
                    </span>
                  </label>
                )}
              </div>

              {/* Role & Order */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">
                    Role
                  </label>
                  <SearchableSelect
                    options={ROLE_OPTIONS}
                    value={signer.role}
                    onChange={(val) => updateSigner(i, { role: val || "client" })}
                    placeholder="Role"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">
                    Signing Order
                  </label>
                  <input
                    type="number"
                    value={signer.order}
                    onChange={(e) =>
                      updateSigner(i, {
                        order: parseInt(e.target.value) || 1,
                      })
                    }
                    min={1}
                    className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:border-emerald-600 transition-colors"
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add another signer - bottom CTA */}
      <button
        type="button"
        onClick={addSigner}
        className="w-full border-2 border-dashed border-slate-200 rounded-xl p-4 text-center hover:border-slate-300 transition-colors group"
      >
        <div className="flex items-center justify-center gap-2 text-sm text-slate-400 group-hover:text-slate-500">
          <Plus className="h-4 w-4" />
          Add Another Signer
        </div>
      </button>
    </div>
  );
};

export default SignersStep;
